import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ScrollText } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { setPageMeta } from '../lib/setPageMeta';
export function Agreement() {
  const {
    t
  } = useLanguage();
  const sections = t('agreementPage.sections') as {
    title: string;
    paragraphs: string[];
  }[];
  const qa = t('agreementPage.qa') as {
    q: string;
    a: string;
  }[];
  useEffect(() => {
    setPageMeta({
      title: 'The Agreement',
      description: 'What an agreement looks like with Dance for Fans — the Investor and Dancer Partnership Agreement in plain terms.'
    });
  }, []);
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#f3e9d2',
    color: '#2a1f15'
  }} data-id="element-167">
      {/* Subtle parchment texture via radial overlays */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" data-id="element-168">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(circle at 20% 15%, rgba(180, 130, 70, 0.10), transparent 50%), radial-gradient(circle at 80% 85%, rgba(140, 60, 40, 0.08), transparent 55%)'
      }} data-id="element-169" />
      </div>

      <div className="relative z-10" data-id="element-170">
        {/* Top bar */}
        <header className="px-6 py-6 border-b" style={{
        borderColor: 'rgba(42, 31, 21, 0.15)'
      }} data-id="element-171">
          <div className="max-w-4xl mx-auto flex items-center justify-between" data-id="element-172">
            <Link to="/" className="inline-flex items-center gap-2 text-sm hover:opacity-70 transition-opacity" style={{
            color: '#2a1f15'
          }} data-id="element-173">
              <ArrowLeft className="w-4 h-4" data-id="element-174" />
              {t('common.backToMain')}
            </Link>
            <span className="text-xs uppercase tracking-[0.3em]" style={{
            color: 'rgba(42, 31, 21, 0.5)'
          }} data-id="element-175">
              {t('agreementPage.headerTag')}
            </span>
          </div>
        </header>

        {/* Hero */}
        <section className="px-6 pt-20 pb-12" data-id="element-176">
          <div className="max-w-3xl mx-auto text-center" data-id="element-177">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-8" style={{
            backgroundColor: 'rgba(140, 35, 35, 0.12)',
            border: '1px solid rgba(140, 35, 35, 0.3)'
          }} data-id="element-178">
              <ScrollText className="w-7 h-7" style={{
              color: '#8c2323'
            }} data-id="element-179" />
            </motion.div>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-xs uppercase tracking-[0.4em] mb-6" style={{
            color: '#8c2323'
          }} data-id="element-180">
              {t('agreementPage.heroTag')}
            </motion.p>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="font-serif text-4xl md:text-6xl leading-[1.1] mb-6" style={{
            color: '#1f1610'
          }} data-id="element-181">
              {t('agreementPage.heroTitle')}
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="font-serif italic text-lg md:text-xl" style={{
            color: 'rgba(42, 31, 21, 0.7)'
          }} data-id="element-182">
              {t('agreementPage.heroSubtitle')}
            </motion.p>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-3xl mx-auto px-6" data-id="element-183">
          <div className="h-px w-24 mx-auto" style={{
          backgroundColor: 'rgba(140, 35, 35, 0.4)'
        }} data-id="element-184" />
        </div>

        {/* Sections */}
        <section className="px-6 py-16" data-id="element-185">
          <div className="max-w-3xl mx-auto space-y-14" data-id="element-186">
            {sections.map((section, i) => <motion.article key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.04
          }} data-id="element-187">
                <div className="flex items-baseline gap-4 mb-5" data-id="element-188">
                  <span className="font-serif text-3xl shrink-0" style={{
                color: 'rgba(140, 35, 35, 0.55)'
              }} data-id="element-189">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h2 className="font-serif text-2xl md:text-3xl leading-tight" style={{
                color: '#1f1610'
              }} data-id="element-190">
                    {section.title}
                  </h2>
                </div>
                <div className="space-y-5 text-base md:text-lg leading-relaxed pl-0 md:pl-12" style={{
              color: 'rgba(42, 31, 21, 0.85)'
            }} data-id="element-191">
                  {section.paragraphs.map((p, j) => <p key={j} className="whitespace-pre-line" data-id="element-192">
                      {p}
                    </p>)}
                </div>
              </motion.article>)}
          </div>
        </section>

        {/* Q&A Section */}
        <section className="px-6 py-16" data-id="element-193">
          <div className="max-w-3xl mx-auto" data-id="element-194">
            <div className="h-px w-24 mx-auto mb-12" style={{
            backgroundColor: 'rgba(140, 35, 35, 0.4)'
          }} data-id="element-195" />
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-center mb-12" data-id="element-196">
              <p className="text-xs uppercase tracking-[0.4em] mb-5" style={{
              color: '#8c2323'
            }} data-id="element-197">
                ✦ Q & A ✦
              </p>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-5" style={{
              color: '#1f1610'
            }} data-id="element-198">
                {t('agreementPage.qaTitle')}
              </h2>
              <p className="font-serif italic text-base md:text-lg" style={{
              color: 'rgba(42, 31, 21, 0.7)'
            }} data-id="element-199">
                {t('agreementPage.qaSubtitle')}
              </p>
            </motion.div>

            <div className="space-y-8" data-id="element-200">
              {qa.map((item, i) => <motion.article key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} className="border-l-2 pl-5 md:pl-7" style={{
              borderColor: 'rgba(140, 35, 35, 0.45)'
            }} data-id="element-201">
                  <div className="flex items-baseline gap-3 mb-3" data-id="element-202">
                    <span className="font-serif text-sm uppercase tracking-[0.25em] shrink-0" style={{
                  color: '#8c2323'
                }} data-id="element-203">
                      Q.
                    </span>
                    <h3 className="font-serif text-xl md:text-2xl leading-snug" style={{
                  color: '#1f1610'
                }} data-id="element-204">
                      {item.q}
                    </h3>
                  </div>
                  <div className="flex items-baseline gap-3" data-id="element-205">
                    <span className="font-serif text-sm uppercase tracking-[0.25em] shrink-0" style={{
                  color: 'rgba(140, 35, 35, 0.7)'
                }} data-id="element-206">
                      A.
                    </span>
                    <p className="text-base md:text-lg leading-relaxed" style={{
                  color: 'rgba(42, 31, 21, 0.85)'
                }} data-id="element-207">
                      {item.a}
                    </p>
                  </div>
                </motion.article>)}
            </div>
          </div>
        </section>

        {/* Closing */}
        <section className="px-6 py-16" data-id="element-208">
          <div className="max-w-2xl mx-auto text-center" data-id="element-209">
            <div className="h-px w-24 mx-auto mb-10" style={{
            backgroundColor: 'rgba(140, 35, 35, 0.4)'
          }} data-id="element-210" />
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="font-serif italic text-xl md:text-2xl mb-8" style={{
            color: 'rgba(42, 31, 21, 0.85)'
          }} data-id="element-211">
              {t('agreementPage.closing')}
            </motion.p>
            <p className="text-xs uppercase tracking-[0.3em]" style={{
            color: 'rgba(140, 35, 35, 0.7)'
          }} data-id="element-212">
              {t('agreementPage.draftNotice')}
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-10 border-t" style={{
        borderColor: 'rgba(42, 31, 21, 0.15)'
      }} data-id="element-213">
          <div className="max-w-4xl mx-auto flex items-center justify-between text-xs" data-id="element-214">
            <span style={{
            color: 'rgba(42, 31, 21, 0.5)'
          }} data-id="element-215">
              © {new Date().getFullYear()} danceforfans.com
            </span>
            <span className="uppercase tracking-[0.3em]" style={{
            color: 'rgba(42, 31, 21, 0.5)'
          }} data-id="element-216">
              {t('agreementPage.footerTag')}
            </span>
          </div>
        </footer>
      </div>
    </div>;
}